import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortState<K extends string | number | symbol> = { field: K | null; dir: SortDirection };

export interface UseSortingOptions<T, K extends string | number | symbol> {
  items: readonly T[];
  accessors: Record<K, (item: T) => string | number>; // alle Werte werden auf string/number reduziert
  initialField?: K | null;
  initialDir?: SortDirection;
}

export interface UseSortingResult<T, K extends string | number | symbol> {
  sortedItems: readonly T[];
  sort: SortState<K>;
  toggleSort: (field: K) => void;
  getSortState: (field: K) => 'none' | 'asc' | 'desc';
  ariaFor: (field: K) => 'none' | 'ascending' | 'descending';
}

export function useSorting<T, K extends string | number | symbol>(opts: UseSortingOptions<T, K>): UseSortingResult<T, K> {
  const { items, accessors, initialField = null, initialDir = 'asc' } = opts;
  const [sort, setSort] = useState<SortState<K>>({ field: initialField, dir: initialDir });

  const sortedItems = useMemo(() => {
    if (!sort.field) return items;
    const accessor = accessors[sort.field];
    if (!accessor) return items;
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    const copy = [...items];
    copy.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // Zahlen direkt vergleichen
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dirMul;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * dirMul;
      if (as > bs) return 1 * dirMul;
      return 0;
    });
    return copy;
  }, [items, sort, accessors]);

  const toggleSort = (field: K) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };

  const getSortState = (field: K): 'none' | 'asc' | 'desc' => {
    if (sort.field !== field) return 'none';
    return sort.dir;
  };

  const ariaFor = (field: K): 'none' | 'ascending' | 'descending' => {
    const st = getSortState(field);
    if (st === 'none') return 'none';
    return st === 'asc' ? 'ascending' : 'descending';
  };

  return { sortedItems, sort, toggleSort, getSortState, ariaFor };
}

